import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Badge, Button, Heading, Paragraph } from "@fronterahealth/frontera-ui-components";

import { getExpertChatMessages, useListExpertChatProviders } from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";

import { ExpertChatTranscript } from "./ExpertChatTranscript";
import { ReassignChatDialog } from "./ReassignChatDialog";
import {
  EXPERT_CHAT_MESSAGE_PAGE_SIZE,
  EXPERT_CHAT_STATUS_LABELS,
  ExpertChatStatus,
  PROVIDER_STALE_TIME_MS,
  UNASSIGNED_PROVIDER_LABEL,
} from "./constants";

export const ExpertChatDetailPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reassignOpen, setReassignOpen] = useState(false);

  // Pages walk backwards: page 0 is the newest window, each further page is
  // the block of messages immediately before it (`until_sequence_number`).
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["expert-chat-messages", chatId],
    enabled: !!chatId,
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await getExpertChatMessages(Number(chatId), {
        limit: EXPERT_CHAT_MESSAGE_PAGE_SIZE,
        ...(pageParam === undefined ? {} : { until_sequence_number: pageParam }),
      });
      if (response.status !== 200) {
        throw new Error(("detail" in response.data && response.data.detail) || "Couldn't load this chat.");
      }
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      // A short page means we reached the start of the conversation.
      if (lastPage.messages.length < EXPERT_CHAT_MESSAGE_PAGE_SIZE) return undefined;
      // Sequences start at 1, so nothing precedes the first message — stopping
      // here avoids both an empty round trip and an out-of-range request.
      const oldest = lastPage.messages[0];
      if (oldest?.sequence_number == null || oldest.sequence_number <= 1) return undefined;
      return oldest.sequence_number - 1;
    },
  });

  const providersQuery = useListExpertChatProviders({
    query: { staleTime: PROVIDER_STALE_TIME_MS, refetchOnWindowFocus: false },
  });
  const providers = providersQuery.data?.status === 200 ? providersQuery.data.data : [];

  const pages = data?.pages;
  const chat = pages?.[0]?.chat;
  // Pages arrive newest-window first; render oldest to newest.
  const messages = useMemo(() => (pages ? [...pages].reverse().flatMap((page) => page.messages) : []), [pages]);

  if (isError) {
    return (
      <div className="flex flex-col bg-bg-default min-h-full px-16 py-8">
        <Breadcrumb crumbs={[{ label: "Expert Chats", to: "/expert-chats" }, { label: "Not found" }]} />
        <Paragraph size="small" colorType="secondary">
          This expert chat could not be loaded.
        </Paragraph>
      </div>
    );
  }

  const isOpen = chat?.status === ExpertChatStatus.OPEN;

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden bg-bg-default px-16 py-8">
      <Breadcrumb crumbs={[{ label: "Expert Chats", to: "/expert-chats" }, { label: chat?.learner_name || "Chat" }]} />

      <div className="mb-6 flex shrink-0 items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <Heading size="medium">{chat?.learner_name || "Expert chat"}</Heading>
            <Badge appearance="neutral" text="Read only" />
            {chat && (
              <Badge
                appearance={isOpen ? "success" : "neutral"}
                text={EXPERT_CHAT_STATUS_LABELS[chat.status as keyof typeof EXPERT_CHAT_STATUS_LABELS]}
              />
            )}
          </div>
          <Paragraph size="small" colorType="secondary">
            {chat?.caregiver_name || "Unknown caregiver"}
            {chat?.caregiver_email ? ` · ${chat.caregiver_email}` : ""}
          </Paragraph>
          <Paragraph size="small" colorType="secondary">
            Assigned to {chat?.assigned_provider_name || UNASSIGNED_PROVIDER_LABEL}
            {chat?.organization_name ? ` · ${chat.organization_name}` : ""}
          </Paragraph>
        </div>

        {isOpen && <Button appearance="secondary" text="Reassign" onClick={() => setReassignOpen(true)} />}
      </div>

      <ExpertChatTranscript
        // Remount per chat: the transcript pins to the newest message once per
        // mount, and this route is reused when only :chatId changes.
        key={chatId}
        messages={messages}
        isLoading={isLoading}
        hasOlder={hasNextPage}
        isLoadingOlder={isFetchingNextPage}
        onLoadOlder={fetchNextPage}
      />

      <ReassignChatDialog
        isOpen={reassignOpen}
        setOpen={setReassignOpen}
        chat={chat ?? null}
        providers={providers}
        isLoadingProviders={providersQuery.isLoading}
        onReassigned={() => {
          // Reassignment closes this chat and the new expert starts a fresh
          // one, so both the list and this cached transcript — whose header
          // carries the status and assigned expert — are out of date.
          void queryClient.invalidateQueries({ queryKey: ["expert-chats"] });
          void queryClient.invalidateQueries({ queryKey: ["expert-chat-messages"] });
          navigate("/expert-chats");
        }}
      />
    </div>
  );
};
